import { FSCollections } from '@src/constants/database';
import Company from '@src/models/Company';
import UserJob from '@src/models/UserJob';
import UserMapper, {
  type UserListSearchField,
  type UserListState,
} from '@src/models/mappers/UserMapper';
import safeExecute from '@src/util/safeExecute';
import type {
  ISendUserInvitationParams,
  ISendUserUpdateParams,
} from '@src/models/interfaces/requests/user';
import type { ElasticQueryParams } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import type {
  IChangePasswordParams,
  ICloudFunctionResult,
  IExternalUserInfo,
  IUpdateProfileData,
  IUserCompany,
  IUserFormData,
  IUserJob,
} from '@src/models/interfaces/user-form.interface';
import { parseSupervisorWorkJourneyConfigIds } from '@src/models/mappers/parseSupervisorWorkJourneys';
import { ACCESS_TYPES } from '@src/packages/utils';
import type { OrgAccessCode } from '@src/packages/utils/access';
import type { JurisdictionIdentifier } from '@src/packages/utils/nationalId';
import {
  trackingWritePayload,
  type TrackingMode,
  type TrackingPrecision,
} from '@src/packages/users/utils/trackingPolicy';
import UserRepository from '@src/repositories/UserRepository';

import { ATTENDANCE_CONTROL_USERS_TYPES } from '@src/constants/users';

import { buildTextSearch } from './utils';

import type { IAllowedDivisions } from '@src/models/interfaces/division.interface';
import type {
  TUserOrganization,
  UserNotificationTarget,
  UserSearchIdsParams,
  UserSearchIdsResult,
} from '@src/models/interfaces/user.interface';

interface IElasticUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  photoURL: string | null;
  access: string;
  divisions: string[];
  idCard?: string;
  disabled?: boolean;
  onboardingCompleted?: boolean;
}

interface IElasticParsedResponse {
  users: IElasticUser[];
  total: number;
  hasMore: boolean;
}

interface ISearchElasticParams {
  orgId: string;
  divsIds?: string[];
  pageSize?: number;
  from?: number;
  searchTerm?: string;
  ids?: string[];
  exactMatch?: boolean;
}

interface IListElasticParams {
  orgId: string;
  divsIds?: string[];
  pageSize?: number;
  from?: number;
  ids?: string[];
  /** Partial, case-insensitive match against the user's display name. */
  searchTerm?: string;
  /** Exact match against email.keyword via term filter. */
  email?: string;
  /** Account state filter, applied server-side. */
  state?: UserListState;
  exactMatch?: boolean;
  /** Overrides which fields `searchTerm` matches (default: firstName + lastName). */
  searchFields?: UserListSearchField[];
}

class UserService {
  private userRepository: UserRepository;

  constructor() {
    this.userRepository = new UserRepository();
  }

  async createUser(
    data: IUserFormData,
    allowedDivisions: IAllowedDivisions[],
  ): Promise<ICloudFunctionResult> {
    return safeExecute(
      async () => {
        // 1. Apply business logic: validate, filter divisions, check access rules
        const formattedData = this.formatUserData(data, allowedDivisions);

        UserService.validateUserFormData(formattedData);

        // 2. Parse domain-specific fields with business rules
        const externalUserInfo = UserService.parseExternalUserInfo(formattedData);
        const job = UserService.parseJob(formattedData);

        // 3. Use Mapper (pure function) to transform to API payload
        const payload = UserMapper.mapToCreatePayload(
          formattedData,
          job,
          externalUserInfo,
        );

        // 4. Structure params according to Cloud Function signature
        const params: ISendUserInvitationParams = {
          organizationId: formattedData.organization.id,
          userCreationParams: payload,
        };

        // 5. Call Repository (which only makes the API call)
        return this.userRepository.create(params);
      },
      'UserService.createUser',
      {
        userId: data?.id || null,
        email: data?.email || null,
        organizationId: data?.organization?.id || null,
      },
    );
  }

  async updateUser(
    data: IUserFormData,
    allowedDivisions: IAllowedDivisions[],
  ): Promise<ICloudFunctionResult> {
    return safeExecute(
      async () => {
        // 1. Apply business logic: validate, filter divisions, check access rules
        const formattedData = this.formatUserData(data, allowedDivisions);

        UserService.validateUserFormData(formattedData);

        // 2. Parse domain-specific fields with business rules
        const externalUserInfo = UserService.parseExternalUserInfo(formattedData);
        const job = UserService.parseJob(formattedData);

        // 3. Use Mapper (pure function) to transform to API payload
        const payload = UserMapper.mapToUpdatePayload(
          formattedData,
          job,
          externalUserInfo,
        );

        // 4. Structure params according to Cloud Function signature
        const params: ISendUserUpdateParams = {
          organizationId: formattedData.organization.id,
          userUpdateParams: payload,
        };

        // 5. Call Repository (which only makes the API call)
        return this.userRepository.update(params);
      },
      'UserService.updateUser',
      {
        userId: data?.id || null,
        email: data?.email || null,
        organizationId: data?.organization?.id || null,
      },
    );
  }

  static validateUserFormData(data: IUserFormData): void {
    if (!data.email) {
      throw new Error('Email is required');
    }
    if (
      data.access !== ACCESS_TYPES.ADMIN &&
      data.access !== ACCESS_TYPES.OWNER &&
      data.access !== ACCESS_TYPES.READER &&
      (data.divisions?.length === 0 || !data?.divisions)
    ) {
      throw new Error('Divisions are required');
    }
  }

  async getOrganizationUsersByIds(organizationId: string, userIds: string[]) {
    return this.userRepository.getOrganizationUsersByIds(organizationId, userIds);
  }

  async getUser(orgId: string, userId: string): Promise<TUserOrganization | null> {
    return this.userRepository.getUser(orgId, userId);
  }

  /**
   * Persists only `jurisdictionId` on the organization user document.
   * Prefer this over `updateUser` when membership fields must stay untouched.
   * Client write is org Owner/Admin only (Firestore rules); same as `user.update`.
   * Validates structure before writing; repository/`safeExecute` surface Firebase errors as-is.
   */
  async updateJurisdictionId(
    organizationId: string,
    userId: string,
    jurisdictionId: JurisdictionIdentifier,
  ): Promise<void> {
    if (
      !jurisdictionId?.countryCode?.trim() ||
      !jurisdictionId?.documentKind?.trim() ||
      !jurisdictionId?.value?.trim()
    ) {
      throw new Error('Invalid jurisdiction identifier structure');
    }

    return this.userRepository.updateJurisdictionId(organizationId, userId, jurisdictionId);
  }

  /**
   * Writes tracking mode/precision on the org user doc (client merge).
   * Used after admin edit so policy persists even if `user.update` omits it.
   */
  async updateTrackingPolicy(
    organizationId: string,
    userId: string,
    trackingMode: TrackingMode,
    trackingPrecision: TrackingPrecision,
  ): Promise<void> {
    if (!organizationId?.trim() || !userId?.trim()) {
      throw new Error('Organization and user are required to update tracking');
    }
    const tracking = trackingWritePayload(trackingMode, trackingPrecision);
    return this.userRepository.updateTrackingPolicy(organizationId, userId, tracking);
  }

  async getNotificationTarget(
    orgId: string,
    userId: string,
  ): Promise<UserNotificationTarget | null> {
    const user = await this.userRepository.getNotificationTarget(orgId, userId);
    return user ? UserMapper.fromNotificationTargetDTO(user) : null;
  }

  async changePassword({
    userId,
    newPassword,
  }: {
    userId: string;
    newPassword: string;
  }): Promise<ICloudFunctionResult> {
    return this.userRepository.changePassword(userId, newPassword);
  }

  async changeUserEmail(userId: string, newEmail: string): Promise<ICloudFunctionResult> {
    return this.userRepository.changeUserEmail(userId, newEmail);
  }

  async confirmEmailChange(token: string): Promise<ICloudFunctionResult> {
    return this.userRepository.confirmEmailChange(token);
  }

  async resendChangeEmail(userId: string): Promise<ICloudFunctionResult> {
    return this.userRepository.resendChangeEmail(userId);
  }

  async getUserByEmail(orgId: string, email: string): Promise<unknown> {
    return this.userRepository.getUserByEmail(orgId, email);
  }

  async updateProfile(data: IUpdateProfileData): Promise<unknown> {
    return this.userRepository.updateProfile(data);
  }

  async updateProfilePassword({
    currentPassword,
    password,
  }: IChangePasswordParams): Promise<ICloudFunctionResult> {
    const response = await this.userRepository.updateProfilePassword({ currentPassword, password });

    if (!response?.success) {
      return { success: false, error: response.error };
    }
    await this.sendEmailByUpdatePassword(response.userId!);
    return { success: true };
  }

  async confirmPassword(currentPassword: string) {
    return this.userRepository.confirmPassword(currentPassword);
  }

  static parseExternalUserInfo(data: IUserFormData): IExternalUserInfo | null {
    const company = new Company(data.company);
    if (!company?.id) return null;

    const access = data.accessReports
      ? ATTENDANCE_CONTROL_USERS_TYPES.SUPERVISOR
      : ATTENDANCE_CONTROL_USERS_TYPES.USER;

    return {
      access,
      company: {
        id: company.id,
        identifier: company.identifier,
        name: company.name,
        tradeName: company.tradeName,
        email: company.email,
        phone: company.phone,
      } satisfies Pick<
        IUserCompany,
        'id' | 'identifier' | 'name' | 'tradeName' | 'email' | 'phone'
      >,
    };
  }

  async sendEmailByUpdatePassword(userId: string): Promise<ICloudFunctionResult> {
    return this.userRepository.sendEmailByUpdatePassword(userId);
  }

  formatUserData(body: IUserFormData, allowedDivisions: IAllowedDivisions[]): IUserFormData {
    const formatted: IUserFormData = {
      ...body,
      idCard: (body.idCard || '').replace(/\./g, ''),
    };

    const adminRoles: readonly OrgAccessCode[] = [
      ACCESS_TYPES.ADMIN,
      ACCESS_TYPES.OWNER,
      ACCESS_TYPES.READER,
    ];
    if (adminRoles.includes(formatted.access)) {
      formatted.divisions = [];
    } else {
      const allowedIds = (allowedDivisions || [])
        .map(({ id }) => id)
        .filter((id): id is string => !!id);
      formatted.divisions = (formatted.divisions || []).filter(id => allowedIds.includes(id));
    }

    return formatted;
  }

  static parseJob(data: IUserFormData): IUserJob {
    const { id, name } = new UserJob(data.job.id, data.job.name);
    return { id, name };
  }

  static parseAddress(data: {
    address?: string;
    city?: string;
    country?: string;
    region?: string;
    zip?: string;
    location?: unknown;
  }) {
    return {
      address: data.address || '',
      city: data.city || '',
      country: data.country || '',
      region: data.region || '',
      zip: data.zip || '',
      location: data.location || null,
    };
  }

  async searchElastic({
    orgId,
    divsIds = [],
    pageSize = 10,
    from = 0,
    searchTerm = '',
    ids = [],
    exactMatch = false,
  }: ISearchElasticParams): Promise<ElasticSearchResponse | null> {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const sort = [{ field: 'createdAt._seconds', order: 'desc' as const }];
    const pagination = { from, size: pageSize };
    const filters: ElasticQueryParams['filters'] = {};
    if (searchTerm) filters.textSearch = buildTextSearch(searchTerm, exactMatch);
    if (ids?.length > 0) filters.terms = { id: ids };

    const params: ElasticQueryParams = {
      entity: FSCollections.USERS,
      organizationId: orgId,
      divisionIds: divsIds,
      filters,
      pagination,
      sort,
    };

    return this.userRepository.listElastic(params);
  }

  async listElastic(
    {
      orgId,
      divsIds,
      pageSize,
      from,
      ids,
      searchTerm = '',
      email = '',
      state = 'all',
      exactMatch = false,
      searchFields,
    }: IListElasticParams,
    parseResponse = true,
  ): Promise<IElasticParsedResponse | ElasticSearchResponse | null> {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const sort = [{ field: 'createdAt._seconds', order: 'desc' as const }];
    const pagination = { from: from ?? 0, size: pageSize ?? 10 };
    const filters = UserMapper.toListElasticFilters({
      searchTerm,
      email,
      state,
      exactMatch,
      searchFields,
      extraFilters: ids?.length ? { terms: { id: ids } } : {},
    });

    const params: ElasticQueryParams = {
      entity: FSCollections.USERS,
      organizationId: orgId,
      divisionIds: divsIds,
      filters,
      pagination,
      sort,
    };

    const response = await this.userRepository.listElastic(params);
    return parseResponse ? this._parseElasticResponse(response) : response;
  }

  /**
   * Resolves organization-user document ids matching list filters in Elastic.
   * Used as the ID source for Firestore `documentId in` realtime queries.
   */
  async searchIds({
    orgId,
    divsIds,
    pageSize = 10,
    from = 0,
    searchTerm = '',
    email = '',
    state = 'all',
    exactMatch = false,
    searchFields,
  }: UserSearchIdsParams): Promise<UserSearchIdsResult | null> {
    return safeExecute(
      async () => {
        if (!orgId) {
          throw new Error('Organization ID is required');
        }

        const filters = UserMapper.toListElasticFilters({
          searchTerm,
          email,
          state,
          exactMatch,
          searchFields,
        });

        const params: ElasticQueryParams = {
          entity: FSCollections.USERS,
          organizationId: orgId,
          divisionIds: divsIds,
          filters,
          pagination: { from, size: pageSize },
          sort: [{ field: 'createdAt._seconds', order: 'desc' }],
          fields: ['id'],
        };

        const response = await this.userRepository.listElastic(params);
        return UserMapper.fromElasticSearchIdsResponse(response);
      },
      'UserService.searchIds',
      { orgId, pageSize, from },
    );
  }

  private _parseElasticUser(hit: { _source?: Record<string, unknown> }): IElasticUser {
    const source = hit?._source || {};
    return {
      id: source.id as string,
      email: source.email as string,
      firstName: source.firstName as string,
      lastName: source.lastName as string,
      displayName: `${source.firstName} ${source.lastName}`,
      photoURL: (source.photoURL as string) || null,
      access: source.access as string,
      divisions: (source.divisions as string[]) || [],
      idCard: (source.idCard as string) || '',
      disabled: source.disabled as boolean | undefined,
      onboardingCompleted: source.onboardingCompleted as boolean | undefined,
    };
  }

  private _parseElasticResponse(response: ElasticSearchResponse | null): IElasticParsedResponse {
    const hits = response?.hits?.hits || [];
    const total = response?.hits?.total?.value || 0;

    return {
      users: hits.map(hit => this._parseElasticUser(hit)),
      total,
      hasMore: hits.length < total,
    };
  }

  async updateUserPermissions(
    orgId: string,
    data: Record<string, unknown>,
  ): Promise<ICloudFunctionResult> {
    return this.userRepository.updateUserPermissions(orgId, data);
  }

  async getUsersWithJourney(params: {
    organizationId: string;
    pageSize?: number;
    pageToken?: string | null;
    workShiftId?: string;
    workJourneyConfigId?: string;
    searchTerm?: string;
    searchFields?: UserListSearchField[];
  }): Promise<unknown> {
    return this.userRepository.getUsersWithJourney(params);
  }

  async getGeneralUserInfo(organizationId: string, userId: string): Promise<ICloudFunctionResult> {
    return this.userRepository.getGeneralUserInfo(organizationId, userId);
  }

  async getSupervisorWorkJourneyConfigIds(userId: string): Promise<string[]> {
    return safeExecute(
      async () => {
        if (!userId) {
          return [];
        }
        const record = await this.userRepository.getUserInfoSingleton(userId);
        return parseSupervisorWorkJourneyConfigIds(record);
      },
      'UserService.getSupervisorWorkJourneyConfigIds',
      { userId },
    );
  }
}

export default UserService;
